import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import type { EligibilityWarning, MissingItem } from "../../types";

/**
 * « Ce qu'il reste à faire » (US-E3-09 step 3) — every item is REQUIRED and blocks submit; the
 * backend never puts a merely-advisory gap in `missing_items` (that is what `eligibility_warnings`
 * is for, rendered as its own "SIGNALÉ" group below and never blocking — same distinction
 * `DossierField`'s own `FieldWarning` already draws on each wizard step).
 *
 * Both groups share the same flat icon+label+action row (Figma `542:25243`, "A0 · Ce qu'il reste
 * à faire") — a warning is exactly as actionable as a required gap, just lower-stakes, so it gets
 * the same row shape rather than a boxed callout that would visually promote it above the items
 * that actually block sending.
 *
 * `navigation_target` is `{section, field_id}` — only `section` is actionable here: there is no
 * scroll-to-field/highlight mechanism built, so a click switches to the right TAB (still real
 * progress towards "each links straight to the field it concerns") rather than doing nothing.
 */
export function ReviewMissingItems({
  items,
  warnings,
  onNavigate,
}: {
  items: MissingItem[];
  warnings: EligibilityWarning[];
  onNavigate: (section: string) => void;
}) {
  const { t } = useTranslation("acquire");

  if (items.length === 0 && warnings.length === 0) return null;

  // Figma's own copy per category: a document gap reopens the uploader ("Ouvrir"), everything
  // else reopens the step it belongs to ("Compléter") — `category` defaults to the section name
  // server-side, so "document" is the one value worth branching on.
  const actionLabel = (category: string) =>
    category === "document"
      ? t("dossier.review.missing-action-document")
      : t("dossier.review.missing-action-default");

  return (
    <section className="flex flex-col gap-md border-t border-b border-(--border-default) bg-surface-card px-md py-lg -mx-md sm:-mx-xl sm:px-xl xl:py-xl">
      <h2 className="text-md-semibold text-(--text-primary)">
        {t("dossier.review.missing-title")}
      </h2>

      {items.length > 0 ? (
        <div className="flex flex-col gap-xs">
          <p className="text-xs-semibold text-(--text-error)">
            {t("dossier.review.missing-required")}
          </p>
          <ul className="flex flex-col">
            {items.map((item) => (
              <li key={`${item.category}:${item.code}:${item.field_id ?? ""}`}>
                <button
                  type="button"
                  onClick={() =>
                    onNavigate(item.navigation_target?.section ?? item.section)
                  }
                  className="flex min-h-11 w-full items-start justify-between gap-sm rounded-md py-sm text-start outline-none hover:bg-surface-page focus-visible:ring-2 focus-visible:ring-focus-ring"
                >
                  <span className="flex items-start gap-xs">
                    <span
                      aria-hidden
                      className="mt-0.5 shrink-0 text-(--feedback-error-icon)"
                    >
                      <Icon name="alert" size={16} />
                    </span>
                    <span className="text-sm-regular text-(--text-primary)">
                      {item.label}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm-semibold text-(--text-link) underline">
                    {actionLabel(item.category)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div className="flex flex-col gap-xs">
          {/* warning-700, not the icon's warning-600 — Figma's own label color for this group. */}
          <p className="text-xs-semibold text-warning-700">
            {t("dossier.review.missing-warning-label")}
          </p>
          <ul className="flex flex-col">
            {warnings.map((warning) => (
              <li key={warning.code}>
                <button
                  type="button"
                  onClick={() => onNavigate(warning.section)}
                  className="flex min-h-11 w-full items-start justify-between gap-sm rounded-md py-sm text-start outline-none hover:bg-surface-page focus-visible:ring-2 focus-visible:ring-focus-ring"
                >
                  <span className="flex items-start gap-xs">
                    <span
                      aria-hidden
                      className="mt-0.5 shrink-0 text-(--feedback-warning-icon)"
                    >
                      <Icon name="alert-triangle" size={16} />
                    </span>
                    <span className="text-sm-regular text-(--text-primary)">
                      {warning.message}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm-semibold text-(--text-link) underline">
                    {t("dossier.review.missing-action-review")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
